import DropdownSheet from 'components/Luxkit/Dropdown/Sheet/DropdownSheet'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Group from 'components/utils/Group'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { validateSemiForm } from 'lib/forms/formUtils'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import React, { ComponentProps, useCallback, useContext, useRef } from 'react'
import OfferCapacityWithAgesSelectBox from './OfferCapacityWithAgesSelectBox'
import OfferCapacityWithAgesSelection from './OfferCapacityWithAgesSelection'
import OfferPageFormattedOccupancy from './OfferPageFormattedOccupancy'
import styled from 'styled-components'
import { generateRoomOccupants } from 'lib/offer/occupancyUtils'

const StyledOfferCapacityWithAgesSelectBox = styled(OfferCapacityWithAgesSelectBox)`
  &.elevated {
    position: relative;
    z-index: 3;
  }
`

// NOTE: Would be nice to have this a separate type
type DropdownPlacement = Pick<ComponentProps<typeof DropdownSheet>, 'placement'>

interface Props extends Pick<ComponentProps<typeof DropdownSheet>, 'boundaryRef' | 'open'> {
  capacities?: Array<App.RoomCapacity>;
  occupantUnit?: 'guest' | 'traveller';
  childrenAgeLabel?: string
  onApply?: (rooms: Array<App.RoomOccupants>, keepBrowsing?: boolean) => void
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disableMultiRoom?: boolean;
  className?: string;
  iconDisabled?: boolean;
  maxRooms?: number;
  dropdownPlacement?: DropdownPlacement;
  dropdownCtaText?: string;
  rooms?: Array<App.RoomOccupants>;
}

function OfferCapacityWithAgesSelect(props: Props) {
  const {
    open,
    capacities,
    occupantUnit = 'guest',
    childrenAgeLabel = 'Age at time of check-in',
    onApply,
    disableMultiRoom,
    onClick,
    boundaryRef,
    className,
    iconDisabled,
    maxRooms,
    rooms,
    dropdownCtaText = 'Apply',
  } = props
  const trigger = useRef(null)
  const container = useRef<HTMLDivElement>(null)
  const dispatch = useOfferPageDispatch()
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const isMobile = useScreenSizeOnly('mobile')
  const dropdownRef = useRef<HTMLDivElement>(null)

  const dismissOccupancyFilter = useCallback(() => {
    dispatch({ type: OfferPageActions.filterContinue })
    trackEvent('OCCUPANCY_FILTERS_CLOSED')
  }, [dispatch, trackEvent])

  const onApplyClose = useCallback(() => {
    if (validateSemiForm(dropdownRef.current!)) {
      dismissOccupancyFilter()
    }
    // if there's no rooms in state, we must still be using the default
    // so pass that through
    onApply?.(rooms ?? [generateRoomOccupants()])
  }, [dismissOccupancyFilter, onApply, rooms])

  const isDropdownOpen = open && !isMobile

  return (
    <div ref={container} className={className}>
      <StyledOfferCapacityWithAgesSelectBox
        ref={trigger}
        className={isDropdownOpen ? 'elevated' : undefined}
        occupantUnit={occupantUnit}
        onClick={onClick}
        multiRoomDisabled={disableMultiRoom}
        iconDisabled={iconDisabled}
      />
      <DropdownSheet
        ref={dropdownRef}
        data-testid="offer-capacity-with-ages-select-dropdown"
        anchorRef={trigger}
        triggerRef={trigger}
        boundaryRef={boundaryRef}
        size="L"
        title="Who is going?"
        open={isDropdownOpen}
        onClose={dismissOccupancyFilter}
        primaryActionProps={{
          children: dropdownCtaText,
          onClick: onApplyClose,
        }}
        footerStart={<Group direction="horizontal-reverse">
          <BodyText variant="large"><OfferPageFormattedOccupancy /></BodyText>
        </Group>}
      >
        <OfferCapacityWithAgesSelection
          maxRooms={maxRooms}
          capacities={capacities}
          childrenAgeLabel={childrenAgeLabel}
          disableMultiRoom={disableMultiRoom}
        />
      </DropdownSheet>
    </div>
  )
}

export default withOfferPageState(
  (state) => ({
    open: state.activeFilter === 'capacity',
    rooms: state.rooms,
  }))(OfferCapacityWithAgesSelect)
